/**
 * Deterministic camera response to damage landing on the platform.
 *
 * The pose is local to the player's view: right/up/forward translation plus roll. It
 * deliberately never changes yaw or pitch, so incoming fire cannot drag the gun off the
 * point the player chose. Simulation time is the only clock.
 */

export type RigImpactCause = 'gunfire' | 'creature';

export interface RigImpactState {
  startedAt: number;
  strength: number;
  /** Source side in camera space, -1 left .. +1 right. */
  side: number;
  cause: RigImpactCause;
}

export interface RigImpactPose {
  right: number;
  up: number;
  forward: number;
  roll: number;
  active: boolean;
}

const ZERO_POSE: RigImpactPose = {
  right: 0,
  up: 0,
  forward: 0,
  roll: 0,
  active: false,
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function smoothstep(value: number): number {
  const t = clamp(value, 0, 1);
  return t * t * (3 - 2 * t);
}

/** Damage is presentation input, bounded so one exceptional hit cannot ruin readability. */
export function rigImpactStrength(damage: number, cause: RigImpactCause): number {
  return cause === 'creature'
    ? clamp(damage / 20, 0.45, 1.20)
    : clamp(damage / 18, 0.22, 1);
}

export function rigImpactPose(time: number, impact: RigImpactState): RigImpactPose {
  const age = time - impact.startedAt;
  const creature = impact.cause === 'creature';
  const duration = creature ? 1.25 : 0.62;
  if (age < 0 || age >= duration) return ZERO_POSE;

  const tailStart = creature ? 0.88 : 0.42;
  const tail = 1 - smoothstep((age - tailStart) / (duration - tailStart));
  const decay = Math.exp(-age * (creature ? 2.6 : 5.8)) * tail;
  const side = clamp(impact.side, -1, 1);
  const strength = impact.strength;

  if (creature) {
    const crush = Math.exp(-age * 4.2);
    return {
      right: strength * decay * (
        side * 0.052 * Math.cos(age * 19)
        + 0.012 * Math.sin(age * 31)
      ),
      up: strength * decay * (0.030 * Math.cos(age * 16 + 0.35) - 0.010),
      forward: -strength * (0.070 * crush + 0.018 * decay * Math.sin(age * 17)),
      roll: -side * strength * decay * 0.0062 * Math.cos(age * 18),
      active: true,
    };
  }

  const strike = Math.exp(-age * 18);
  return {
    right: strength * decay * (
      side * 0.027 * Math.cos(age * 63)
      + 0.008 * Math.sin(age * 91)
    ),
    up: strength * decay * (0.012 * Math.cos(age * 51) - 0.006),
    forward: -strength * (0.026 * strike + 0.009 * decay * Math.sin(age * 47)),
    roll: -side * strength * decay * 0.0035 * Math.cos(age * 54),
    active: true,
  };
}
